'use client';

import type {
  ReactNode} from 'react';
import {
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react';
import type { Application } from '../store/projectStore';

export interface Company {
  id: string;
  name: string;
  tenantId: string | null;
}

export interface ProjectConfig {
  companyId: string;
  applicationId: string;
}

interface ProjectContextType {
  // Current selection
  companyId: string;
  applicationId: string;

  // Selected objects
  selectedCompany?: Company;
  selectedTarget?: Application;

  // Actions
  updateProject: (config: Partial<ProjectConfig>) => void;
  setCompany: (company: Company) => void;
  setTarget: (application: Application) => void;
  clearProject: () => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const STORAGE_KEY = 'terra-proxy-project-config';

interface ProjectProviderProps {
  children: ReactNode;
}

export function ProjectProvider({ children }: ProjectProviderProps) {
  const [companyId, setCompanyId] = useState<string>('');
  const [applicationId, setApplicationId] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>();
  const [selectedTarget, setSelectedApplication] = useState<
    Application | undefined
  >();

  // Load from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedConfig = localStorage.getItem(STORAGE_KEY);
      if (savedConfig) {
        try {
          const config = JSON.parse(savedConfig);
          setCompanyId(config.companyId || '');
          setApplicationId(config.applicationId || '');
          setSelectedCompany(config.selectedCompany);
          setSelectedApplication(config.selectedTarget);
        } catch (error) {
          console.warn('Failed to parse saved project config:', error);
        }
      }
    }
  }, []);

  // Save to localStorage whenever config changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const config = {
        companyId,
        applicationId,
        selectedCompany,
        selectedTarget,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    }
  }, [companyId, applicationId, selectedCompany, selectedTarget]);

  const updateProject = (config: Partial<ProjectConfig>) => {
    if (config.companyId !== undefined) {
      setCompanyId(config.companyId);
    }
    if (config.applicationId !== undefined) {
      setApplicationId(config.applicationId);
    }
  };

  const setCompany = (company: Company) => {
    setCompanyId(company.id);
    setSelectedCompany(company);
    // Clear application when company changes
    setApplicationId('');
    setSelectedApplication(undefined);
  };

  const setTarget = (application: Application) => {
    setApplicationId(application.id);
    setSelectedApplication(application);
  };

  const clearProject = () => {
    setCompanyId('');
    setApplicationId('');
    setSelectedCompany(undefined);
    setSelectedApplication(undefined);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const value: ProjectContextType = {
    companyId,
    applicationId,
    selectedCompany,
    selectedTarget,
    updateProject,
    setCompany,
    setTarget,
    clearProject,
  };

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
}

export function useProject() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}
